/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable complexity */
import { useContext, useMemo, useState, useCallback, useRef, useEffect, memo } from 'react';
import { InteractionStepTaskComponent } from 'react-scale/components/scalers/tasks/ThreadedInteraction/InteractionStep';
import {
  DEFAULT_PROCESS_SUPERVISION_SUBTITLE,
  DEFAULT_PROCESS_SUPERVISION_TITLE,
  InteractionStepType,
  IProcessSupervisionChunk,
  ProcessSupervisionStepResponse,
  ContextType,
} from '@scale/shared/types/chat';
import { twMerge } from 'tailwind-merge';

import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import {
  Icon,
  Typography,
  Markdown,
  TextArea,
  Button,
  SelectMenu,
  Skeleton,
  Tag,
  Accordion,
} from '@scale/scaleui';
import { Alert } from '../../TempComponents';
import _ from 'lodash';
import { useAsync } from 'react-use-v13';
import {
  useAllResponsesForStepType,
  useChatStoreExtract,
  useMostRecentResponseForStepType,
  useMostRecentResponseFullObject,
  useStepParams,
  useStepResponse,
} from '../../../Chat/hooks/ChatStore.hooks';
import { useStepSubmitHandler } from '../../hooks/useStepSubmitHandler';
import { useConfigureChatMessageDisplay } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useConfigureChatMessageDisplay';

import { StepWidthWrapper, StepWidth } from '../../step/StepWidthWrapper';
import {
  experimentalToolUseIsComplete,
  experimentalGetStepOutput,
  getExperimentalToolUseShouldResetHistory,
} from './helpers';
import {
  ProcessChunkingStrategy,
  ProcessChunkingStrategyMap,
  SubProcessChunkingStrategyMap,
} from './chunking';
import {
  DEFAULT_PROCESS_CHUNKING_STRATEGY,
  DEFAULT_RATING_BUTTONS,
  ProcessSupervisionChunk,
  DEFAULT_SUB_CHUNKING_STRATEGY_PARAMS,
  DEFAULT_SUB_CHUNKING_STRATEGY,
  DEFAULT_EXPERIMENTAL_TOOL_USE_FIELDS_TO_COLLECT,
  TOOLS,
} from './types';
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd';

import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

import { StepCard } from '../../step/StepCard';
import { PinButton } from '../../step/PinButton';
import { ChatMessageDisplayConfigButton } from '../../step/ChatMessageDisplayConfig';
import { CommentButton } from '../../feedback/CommentButton';
import { constructStepChildId } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import MarkdownEditorContext from '../../components/editor/MarkdownEditorContext';
import { Editor, EditorContainer, MarkdownFloatingMenu } from '../../components/editor';
import MarkdownToolbar from '../../components/editor/toolbar/MarkdownToolbar';
import { useMarkdownSyntaxOptions } from '../../hooks/useMarkdownSyntaxOptions';

const ExperimentalToolUseTaskComponent: InteractionStepTaskComponent = ({
  readOnly,
  complete,
  responseIndex,
  isPinnedView,
}) => {
  const step = useStepParams<InteractionStepType.ExperimentalToolUse>(responseIndex);
  const taskContext = useContext(ChatTaskContext);
  const markdownSyntaxConfig = useMarkdownSyntaxOptions();

  const toolOptions = step.params?.tools || TOOLS;

  // TODO(@averyyip): Abstract away tools -> convert to operator config
  const toolParamsMap: Record<string, string[]> = {
    calculator: ['operation'],
    date: [],
    current_weather: ['city_name', 'country_code'],
    historical_weather: ['city_name', 'country_code', 'start_date', 'end_date'],
    wiki_search: ['query', 'num_results'],
    wolfram_alpha: ['query'],
    google_search: ['query', 'location'],
    time_series_intraday: ['symbol', 'interval', 'month'],
    time_series_daily: ['symbol', 'number_of_days'],
    ticker_search: ['keywords'],
    python_interpreter: ['code'],
    finish: ['answer'],
  };

  // Params and their defaults
  // TODO: ignore and remove
  const injectTaskerLocation =
    step.params?.process_chunking_strategy_params?.useWorkerLocation ?? false;

  // TODO: Chunking strategy -> operator config
  const processChunkingStrategyName = (step?.params?.process_chunking_strategy ||
    DEFAULT_PROCESS_CHUNKING_STRATEGY) as ProcessChunkingStrategy;
  let processChunkingStrategyParams = step?.params?.process_chunking_strategy_params || {};

  // TODO: Subchunking -> operator config
  const subChunkingStrategyName =
    step?.params?.sub_chunking_strategy || DEFAULT_SUB_CHUNKING_STRATEGY;
  const subChunkingStrategyParams =
    step?.params?.sub_chunking_strategy_params || DEFAULT_SUB_CHUNKING_STRATEGY_PARAMS;
  const subChunkingKeys =
    subChunkingStrategyParams.keys || DEFAULT_SUB_CHUNKING_STRATEGY_PARAMS.keys;
  const subChunkingEditMask =
    // generalize way to show which parts need editing. Observation didn't need to be edited
    // e.g. thought, action, action input needs editing. observation does not
    // action, action input are a must, some might bypass thought
    subChunkingStrategyParams.edit_mask || DEFAULT_SUB_CHUNKING_STRATEGY_PARAMS.edit_mask;
  const useSubChunkingStrategy = step?.params?.use_sub_chunking_strategy || false;

  // NOTE: Can I remove this? yes
  if (injectTaskerLocation) {
    const workerId = taskContext.workerId;
    processChunkingStrategyParams = {
      ...processChunkingStrategyParams,
      workerId,
      useWorkerLocation: true,
    };
  }

  // Question: What is this?
  // Note: each button has a rating 1 or -1. Buttons are configurable in the configs.
  // If rating is clicked, we don't want to annotate future steps.
  // Say we want to show 4 react steps at a time

  // Question: We currently do it iteratively for tool use process superviz. Do we want to show the whole chain upfront?
  // Say we want to show all steps upfront. Say step 2, cb finds an incorrect step. Regeneration will only show step 3 moving forward.
  const endAfterRatingIds = useMemo(
    () => step?.params?.end_after_rating_ids || [-1],
    [step?.params?.end_after_rating_ids],
  );

  // Question: What is this?
  // note: should be in operator config, look at basic superviz template in mayforce demo
  const reRequestModel = step?.params?.re_request_model ?? true;

  // Question: What is this? Correct don't collect anything, incorrect we collect a few things
  // the one that does has functional meaning is rewrite. It'll take that value and use it to re-request the model
  const rewriteFieldId = 'rewrite';

  // Note: Refactor rating buttons
  const ratingButtons = step.params?.rating_buttons || DEFAULT_RATING_BUTTONS;
  const ratingValues = ratingButtons.map((rating: any) => rating.value);
  const ratingValueToLabel = (value: number) => {
    const button = ratingButtons.find((rating: any) => rating.value === value);
    return button?.label;
  };

  // Question: What is this? Fields to collect for the incorrects
  // hard code fields to start
  const fieldsToCollect = DEFAULT_EXPERIMENTAL_TOOL_USE_FIELDS_TO_COLLECT;

  // Question: What is this used for?
  // NOTE: taskContext comes from chat task context. It's a project config
  const enableLatex = taskContext.milkdownEditorConfig?.enableMathPlugin ?? false;

  // Question: What is this?
  // Note: if they want to override how latex is rendered
  // TODO: Just ignore for now, we can add later
  const {
    chatMessageDisplayConfig,
    useMarkdownDisplay,
    updateMarkdownConfig,
    toggleChatMessageDisplayType,
  } = useConfigureChatMessageDisplay();

  // Todo: Can we remove this? Yes
  const shouldResetHistoryFeatureFlagEnabled = useUnleashFeatureFlag(
    'chat-superviz-use-reset-history',
    { projectId: taskContext.projectId },
    false,
  );

  // Misc.
  // Question: What is this used for?
  const milkdownRef = useRef<{ update: (text: string) => void }>(null);
  const useMilkdown = true;

  // Question: What is this?
  // Note: chat config, store to pull them. templateVars, number of max turns
  const config = useChatStoreExtract(state => state.config);
  // Question: What is this?
  // NOTE: when you call onSubmit, it propagates to the store. This should be used to render the progress so far
  const response = useStepResponse<ProcessSupervisionStepResponse>(responseIndex);

  // Question: What is this?
  const { onSubmit } = useStepSubmitHandler({
    responseIndex,
  });

  // Note: Looking at past prompts and responses
  const mostRecentPrompt = useMostRecentResponseForStepType(
    InteractionStepType.PromptInput,
    responseIndex,
  );

  const previousResponses = useAllResponsesForStepType(
    InteractionStepType.ExperimentalToolUse,
    responseIndex,
  );

  // Todo: What is this? Can I remove?
  // It's for the re-requesting logic. We added a finish action to manually complete the step as opposed to whether it's solved by rewrite.
  // vanilla supervision: we let the CB if it's complete
  const [isQuestionSolvedByRewrite, setIsQuestionSolvedByRewrite] = useState<boolean | undefined>(
    response?.context?.isQuestionSolvedByRewrite ?? undefined,
  );

  // Question: What is this?
  const chatResponse = useMostRecentResponseFullObject(responseIndex);
  const candidate = useMemo(() => {
    return chatResponse?.context?.candidates?.[0];
  }, [chatResponse]);

  // State
  // Note: processSteps contains all in vanilla superviz
  const [processSteps, setProcessSteps] = useState<ProcessSupervisionChunk[]>([]); // new step, this is an array of 1 item right now
  const [correctedSteps, setCorrectedSteps] = useState<ProcessSupervisionChunk[]>(); // reviewed by cb and corrected
  const [actionPlanChunk, setActionPlanChunk] = useState<ProcessSupervisionChunk>(); // action plan is a separate entity in itself
  const [isEditingProcessSteps, setIsEditingProcessSteps] = useState(false);
  const [previousCorrectedStepsLength, setPreviousCorrectedStepsLength] = useState<number>(0); // used for opacity / readonly

  // STATE
  // NOTE: Need walkthrough of logic
  const {
    loading: processStepsIsLoading,
    error: processStepsError,
    value: processStepsResponse,
  } = useAsync(async () => {
    // Process steps
    const processChunkingStrategy = ProcessChunkingStrategyMap[processChunkingStrategyName];
    if (!candidate) {
      return [];
    }

    // NOTE: chunks from the most recent model response
    const processChunkingStrategyPromise = processChunkingStrategy(
      candidate?.message?.content || '',
      processChunkingStrategyParams,
    );

    const { chunks } = await processChunkingStrategyPromise;
    const actionPlanContent = chunks[0].slice('**Step 0:** '.length);
    chunks.shift();

    if (useSubChunkingStrategy) {
      const subProcessChunkingStrategy = SubProcessChunkingStrategyMap[subChunkingStrategyName];
      const subProcessChunkingStrategyPromise = subProcessChunkingStrategy(
        chunks,
        subChunkingKeys,
        subChunkingEditMask,
      );
      const { subChunks } = await subProcessChunkingStrategyPromise;

      const fullChunks = subChunks.filter(chunk => chunk).map(chunk => ({ content: chunk }));

      const allPreviousCorrectChunks = previousResponses
        .map(response => response?.context?.correctChunks || [])
        .filter(Boolean) as IProcessSupervisionChunk[][];

      // account for the action plan chunk, which is processed seperately
      const previousCorrectChunksLength = Math.max(
        _.flatten(allPreviousCorrectChunks).length - 1,
        0,
      );
      const chunksToDisplay: IProcessSupervisionChunk[] = fullChunks.slice(
        previousCorrectChunksLength,
      );
      const chunksAlreadyCorrect: IProcessSupervisionChunk[] = fullChunks.slice(
        0,
        previousCorrectChunksLength,
      );

      let actionPlanChunk: IProcessSupervisionChunk = { content: actionPlanContent };
      // if prevous responses exists, then we update the ratings and rewrite fields

      if (response?.context?.correctChunksAggregated?.length) {
        const currActionPlanChunk = response?.context?.correctChunksAggregated[0];
        const currChunksToDisplay = response?.context?.correctChunksAggregated.slice(
          previousCorrectChunksLength + 1,
        );
        actionPlanChunk = {
          content: actionPlanChunk.content,
          rating: currActionPlanChunk.rating,
          rewrite: currActionPlanChunk.rewrite?.slice('**Step 0:** '.length),
        };
        currChunksToDisplay.forEach((chunk: Record<string, any>, index) => {
          Object.keys(chunk).forEach((key: string) => {
            if (key !== 'content') {
              chunksToDisplay[index] = {
                ...chunksToDisplay[index],
                [key.replace('__DOLLAR__', '$')]: chunk[key],
              };
            }
          });
          chunksToDisplay[index].rating = chunk.rating;
          if (index % subChunkingKeys.length === 0) {
            chunksToDisplay[index].rewrite = chunk.rewrite?.slice(`**Step ${index + 1}:** `.length);
          } else {
            chunksToDisplay[index].rewrite = chunk.rewrite;
          }
          chunksToDisplay[index].rewrite = chunksToDisplay[index].rewrite
            ?.slice(`**${subChunkingKeys[index % subChunkingKeys.length]}:** `.length)
            .trim();
        });
      }

      setActionPlanChunk(actionPlanChunk);
      setPreviousCorrectedStepsLength(allPreviousCorrectChunks.length);
      setCorrectedSteps(chunksAlreadyCorrect);
      setProcessSteps(chunksToDisplay); // List of 4 things: [thought, action, action input, observation][]
      // TODO: Tighten the typing. Array of objects
      return fullChunks;
    } else {
      const res = chunks.map(chunk => ({ content: chunk }));
      setProcessSteps(res);
      return res;
    }
  });

  // Question: What is this?
  const setFieldOnChunk = useCallback(
    (field: string, index: number, value: any) => {
      setProcessSteps(prevProcessSteps =>
        prevProcessSteps.map((step, i) => (i === index ? { ...step, [field]: value } : step)),
      );
    },
    [setProcessSteps],
  );

  // Question: What is this?
  // NOTE: see if we need it. not most critical
  // NOTE: makes it an easy interface to determine if they've filled out a field. use for the save and continue
  // will eventually deprecate
  const fieldEntries: Record<keyof ProcessSupervisionChunk, any[]> = useMemo(() => {
    const out: Record<keyof ProcessSupervisionChunk, any[]> = {};
    processSteps.forEach(step => {
      if (!out.rating) {
        out.rating = [];
      }
      out.rating.push(step.rating ?? undefined);

      fieldsToCollect.forEach(field => {
        const fieldId = field.fieldId;

        const value = step[fieldId];
        if (!out[fieldId]) {
          out[fieldId] = [];
        }
        out[fieldId].push(value ?? undefined);
      });
    });
    return out;
  }, [fieldsToCollect, processSteps]);

  // Todo: Can I remove this?
  // These are temporary memos and setters to preserve backwards compatibility before adding custom fields
  // This will be deprecated soon
  const ratings = useMemo(() => fieldEntries.rating || [], [fieldEntries.rating]);
  const rewrites = useMemo(
    () => fieldEntries[rewriteFieldId] || [],
    [fieldEntries, rewriteFieldId],
  );

  const rewriteReasons = useMemo(
    () => fieldEntries.rewriteReason || [],
    [fieldEntries.rewriteReason],
  );

  const setRatings = useCallback(
    (index: number, value: number) => {
      setFieldOnChunk('rating', index, value);
    },
    [setFieldOnChunk],
  );

  const setRewrites = useCallback(
    (index: number, value: string | undefined) => {
      setFieldOnChunk(rewriteFieldId, index, value);
    },
    [setFieldOnChunk, rewriteFieldId],
  );

  // Question: What is this?
  // NOTE: Don't want annotations after the first bad rating. Used to disable subsequent steps.
  // If they have a rewrite, they put on step 7. If they marked incorrect in previous steps, we just drop the data for all after.
  // find the first bad rating index, where rating < 0
  const firstBadRatingIndex = useMemo(() => {
    if (!endAfterRatingIds?.length) {
      return 100000;
    }

    const num = Object.entries(ratings).find(([_index, rating]) =>
      endAfterRatingIds?.includes(rating),
    )?.[0];

    if (useSubChunkingStrategy) {
      let subStepIndex: number = 100000;
      if (num) {
        subStepIndex =
          ((parseInt(num) + 1) % subChunkingKeys.length) * (subChunkingKeys.length + 1);
      }
      return subStepIndex;
    } else {
      return num ? parseInt(num) : 100000;
    }
  }, [ratings, endAfterRatingIds, useSubChunkingStrategy]);

  const [error, setError] = useState<string | undefined>('');

  // Question: What is this?
  // NOTE: list of required fields.
  const coreFields = useMemo(() => ['rating', rewriteFieldId, 'rewriteReason'], [rewriteFieldId]);

  // Question: What is this?
  // NOTE: shows wihch fields we want to render based on configs
  // for example, only make it visible if it's a certain rating.
  // used to determine what fields are requested for a given rating
  const getFieldsToCollectAtIndex = useCallback(
    (index: number) => {
      const fieldsToCollectAtIndex = [];
      for (const field of fieldsToCollect) {
        if (firstBadRatingIndex < index) {
          continue;
        }
        // TODO later: check if field is required/should be conditionally shown in params
        if (field.collectForRatingValues.includes(ratings[index])) {
          fieldsToCollectAtIndex.push(field);
        }
      }
      return fieldsToCollectAtIndex;
    },
    [fieldsToCollect, firstBadRatingIndex, ratings],
  );

  const isEmpty = (type: string, value: any) => {
    if (type === 'markdown' || type === 'plaintext') {
      return !value || value === '';
    }
    return _.isUndefined(value);
  };

  // Question: What is this?
  // NOTE: does the actual validation
  const getFieldsAreCompleteAtIndex = useCallback(
    (index: number) => {
      const fieldsToCollectAtIndex = getFieldsToCollectAtIndex(index);
      if (fieldsToCollectAtIndex.length === 0) {
        return { fieldsAreComplete: true };
      }

      // loop through each field at that processSteps index and check if its defined
      for (const field of fieldsToCollectAtIndex) {
        const fieldId = field.fieldId;
        if (isEmpty(field.type, fieldEntries?.[fieldId]?.[index]) && field.required) {
          return { fieldsAreComplete: false, fieldId };
        }
      }

      return { fieldsAreComplete: true };
    },
    [fieldEntries, getFieldsToCollectAtIndex, isQuestionSolvedByRewrite, reRequestModel],
  );

  // Question: What is this?
  const getErrorMessage = useCallback(() => {
    let errorMessage = '';
    if (actionPlanChunk?.rating === -1) {
      return '';
    }
    if (!processSteps?.length) {
      errorMessage = '';
    } else {
      // check if all of the ratings up to the first bad rating are defined
      for (let i = 0; i < Math.min(firstBadRatingIndex, processSteps.length); i++) {
        if (!subChunkingEditMask[i % subChunkingKeys.length]) {
          continue;
        }
        const ratingIsDefined = !_.isUndefined(processSteps[i].rating);
        if (!ratingIsDefined) {
          return `Please rate all steps`;
        }
      }
    }
    return '';
  }, [
    complete,
    processSteps,
    ratings,
    firstBadRatingIndex,
    getFieldsAreCompleteAtIndex,
    actionPlanChunk,
  ]);

  let readOnlyCorrectChunks: JSX.Element[] = [];
  // Question: What is this?
  // NOTE: Populates the history of corrected steps.
  if (correctedSteps) {
    readOnlyCorrectChunks = correctedSteps.map((chunk: ProcessSupervisionChunk, index: number) => {
      return (
        <div
          key={`last-corrected-${index}`}
          className={`flex flex-col gap-1 w-full items-center justify-between ${
            (index + 1) % subChunkingKeys.length ? 'border-b border-neutral-200' : ''
          } py-2 opacity-50`}
        >
          <div className="flex flex-row gap-4 items-center w-full justify-between ">
            <Typography.Subtitle3 className="flex flex-col items-start gap-2">
              <Tag className="" onClick={function noRefCheck() {}} size="sm" variant="primary">
                {subChunkingKeys[index % subChunkingKeys.length].toUpperCase().replace('_', ' ')}
              </Tag>

              <Markdown
                className={`text-sm`}
                markdown={chunk.content}
                enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
                enableHtml={taskContext.htmlMarkdownConfig?.enablePromptPreviews}
                enableSingleDollarTextMath={
                  chatMessageDisplayConfig.markdownConfig.enableSingleDollarTextMath
                }
                enableSoftBreaks={true}
              />
              {rewrites?.[index] && !useMilkdown && (
                <Markdown markdown={rewrites?.[index] || ''} enableMath={enableLatex} />
              )}
            </Typography.Subtitle3>
          </div>
        </div>
      );
    });
  }

  // Question: What is this?
  // Wraps up the previous step into an accordion
  readOnlyCorrectChunks = _.compact(
    readOnlyCorrectChunks.map((chunk, index) => {
      if (index % subChunkingKeys.length === 0) {
        return (
          <Accordion
            key={`accordion-${index}`}
            header={
              <Tag className="" onClick={function noRefCheck() {}} size="lg" variant="neutral">
                STEP {Math.floor(index / subChunkingKeys.length) + 1}
              </Tag>
            }
            target={readOnlyCorrectChunks.slice(index, index + subChunkingKeys.length)}
          />
        );
      }
    }),
  );

  // Question: What is this?
  // NOTE: this is the new steps. Always just 1 step currently because of the iterative approach.
  let processStepComponents = processSteps.map((chunk: ProcessSupervisionChunk, index: number) => {
    const isLastItem = index === processSteps.length - 1;
    const correctIndex = index - 1;
    return (
      <div
        key={index}
        className={`flex flex-col gap-1 w-full items-center justify-between ${
          isLastItem
            ? ''
            : (index + 1) % subChunkingKeys.length
            ? 'border-b border-neutral-200'
            : 'border-b-4 border-neutral-500'
        } py-2 ${
          index > firstBadRatingIndex || actionPlanChunk?.rating === -1 ? 'opacity-50' : ''
        }`}
      >
        <div className="flex flex-row gap-4 items-center w-full justify-between ">
          <Typography.Subtitle3 className="flex flex-col items-start gap-2">
            {!(index % subChunkingKeys.length) && (
              <Tag className="" onClick={function noRefCheck() {}} size="lg" variant="neutral">
                STEP{' '}
                {Math.floor((correctedSteps?.length || 0 + index) / subChunkingKeys.length) + 1}
              </Tag>
            )}
            <Tag className="" onClick={function noRefCheck() {}} size="sm" variant="primary">
              {subChunkingKeys[index % subChunkingKeys.length].toUpperCase().replace('_', ' ')}
            </Tag>

            <Markdown
              className={`text-sm ${chunk.rating === -1 ? 'opacity-50' : ''}`}
              markdown={chunk.content}
              enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
              enableHtml={taskContext.htmlMarkdownConfig?.enablePromptPreviews}
              enableSingleDollarTextMath={
                chatMessageDisplayConfig.markdownConfig.enableSingleDollarTextMath
              }
              enableSoftBreaks={true}
            />
            {/* NOTE: If there is rewrites, show the rewrite */}
            {rewrites?.[index] && !useMilkdown && (
              <Markdown markdown={rewrites?.[index] || ''} enableMath={enableLatex} />
            )}
          </Typography.Subtitle3>
          {/* NOTE: renders the buttons only if it is editable */}
          {subChunkingEditMask[index % subChunkingKeys.length] === 1 && (
            <div className="max-w-[300px] min-w-[200px] flex flex-wrap items-end flex-row-reverse gap-1">
              {ratingValues.map(value => {
                const buttonLabel = ratingValueToLabel(value).toString();
                const isSelected = ratings?.[index]?.toString() === value.toString();

                const variant = ratingButtons.find((rating: any) => rating.value === value)?.color;

                return (
                  <Button
                    key={value.toString()}
                    onClick={() => {
                      if (readOnly || index > firstBadRatingIndex) {
                        return;
                      }
                      setRatings(index, value);
                    }}
                    variant={isSelected ? variant : 'transparent'}
                    // TODO ignore if we are not chaining processes together
                    disabled={index > firstBadRatingIndex || actionPlanChunk?.rating === -1}
                    className={twMerge(
                      `inline-flex px-2.5 py-1.5 text-sm items-center rounded-lg font-medium transition-all justify-center`,
                    )}
                  >
                    <Typography.Subtitle3>{buttonLabel}</Typography.Subtitle3>
                  </Button>
                );
              })}
            </div>
          )}
        </div>
        {/* NOTE: shows text area */}
        {chunk.rating === -1 && index % subChunkingKeys.length === 0 && (
          <div className="flex flex-col gap-2 items-start w-full border border-neutral-200 rounded-lg p-4">
            <MarkdownEditorContext
              defaultTokens={markdownSyntaxConfig}
              initialContent={chunk.rewrite || chunk.content}
            >
              <EditorContainer>
                <MarkdownToolbar />
                <MarkdownFloatingMenu />
                <Editor
                  content={chunk.rewrite || chunk.content}
                  onContentChange={newValue => {
                    if (index > firstBadRatingIndex || readOnly) {
                      return;
                    }
                    setRewrites(index, newValue);
                  }}
                  readOnly={index > firstBadRatingIndex || readOnly}
                />
              </EditorContainer>
            </MarkdownEditorContext>
          </div>
        )}
        {/* NOTE: shows the action being edited */}
        {chunk.rating === -1 && index % subChunkingKeys.length === 1 && (
          <div key={index} className="flex flex-col gap-2 items-start w-full">
            <div className="flex flex-col gap-2 items-start w-full border border-neutral-200 rounded-lg p-4">
              <SelectMenu
                options={toolOptions.map((option: string) => ({
                  id: option,
                  name: option,
                }))}
                selectedOptionId={chunk.rewrite || chunk.content}
                setSelected={newValue => {
                  if (index > firstBadRatingIndex || readOnly) {
                    return;
                  }
                  setRewrites(index, newValue);
                }}
                disabled={index > firstBadRatingIndex || readOnly}
                className="w-[240px]"
                variant="default"
              />
            </div>
          </div>
        )}
        {/* NOTE: shows the action input being edited. shows N many editors depending on the number of parameters for that action tool */}
        {chunk.rating === -1 &&
          index % subChunkingKeys.length === 2 &&
          toolParamsMap[processSteps[index - 1].rewrite || processSteps[index - 1].content]
            ?.length !== 0 && (
            <div className="flex flex-col gap-2 items-start w-full border border-neutral-200 rounded-lg p-4">
              {toolParamsMap[
                processSteps[index - 1].rewrite || processSteps[index - 1].content
              ]?.map((param: string) => {
                const selectedTool =
                  processSteps[index - 1].rewrite || processSteps[index - 1].content;

                let currText = '';
                if (selectedTool === 'python_interpreter') {
                  if (chunk[`${selectedTool}$${param}`]) {
                    currText = '```python\n' + chunk[`${selectedTool}$${param}`] + '\n```';
                  } else {
                    currText = '```python\n\n```';
                  }
                } else {
                  currText = chunk[`${selectedTool}$${param}`] || '';
                }

                return (
                  <div key={index} className="flex flex-col gap-2 items-start w-full">
                    <Tag
                      className=""
                      onClick={function noRefCheck() {}}
                      size="lg"
                      variant="neutral"
                    >
                      {param}
                    </Tag>
                    <MarkdownEditorContext
                      defaultTokens={markdownSyntaxConfig}
                      initialContent={currText}
                    >
                      <EditorContainer>
                        <MarkdownToolbar />
                        <MarkdownFloatingMenu />
                        <Editor
                          content={currText}
                          onContentChange={newValue => {
                            if (index > firstBadRatingIndex || readOnly) {
                              return;
                            }

                            const newValueFormatted = newValue
                              .trim()
                              .replace(/&#x20;/g, ' ')
                              .replace('```python\n', '')
                              .replace('\n```', '')
                              .replace(/\t/g, '    ')
                              .replace(/\r\n/g, '\n')
                              .trim();

                            setFieldOnChunk(`${selectedTool}$${param}`, index, newValueFormatted);
                          }}
                          readOnly={index > firstBadRatingIndex || readOnly}
                        />
                      </EditorContainer>
                    </MarkdownEditorContext>
                  </div>
                );
              })}
            </div>
          )}
      </div>
    );
  });

  processStepComponents = readOnlyCorrectChunks.concat(processStepComponents);

  // Question: What is this?
  // NOTE: Renders the action plan
  if (actionPlanChunk) {
    let actionPlanComponent = (
      <div
        key={0}
        className={`flex flex-col gap-1 w-full items-center justify-between py-2 ${
          previousCorrectedStepsLength !== 0 ? 'opacity-50' : ''
        }`}
      >
        <div className="flex flex-row gap-4 items-center w-full justify-between ">
          <Typography.Subtitle3 className="flex flex-col items-start gap-2">
            <Tag className="" onClick={function noRefCheck() {}} size="lg" variant="primary">
              ACTION PLAN
            </Tag>
            <Markdown
              className={`text-sm`}
              markdown={actionPlanChunk.content}
              enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
              enableHtml={taskContext.htmlMarkdownConfig?.enablePromptPreviews}
              enableSingleDollarTextMath={
                chatMessageDisplayConfig.markdownConfig.enableSingleDollarTextMath
              }
              enableSoftBreaks={true}
            />
            {actionPlanChunk.rewrite && !useMilkdown && (
              <Markdown markdown={actionPlanChunk.rewrite || ''} enableMath={enableLatex} />
            )}
          </Typography.Subtitle3>
          {previousCorrectedStepsLength === 0 && (
            <div className="max-w-[300px] min-w-[200px] flex flex-wrap items-end flex-row-reverse gap-1">
              {ratingValues.map(value => {
                const buttonLabel = ratingValueToLabel(value).toString();
                const isSelected = actionPlanChunk.rating?.toString() === value.toString();

                const variant = ratingButtons.find((rating: any) => rating.value === value)?.color;

                return (
                  <Button
                    key={value.toString()}
                    onClick={() => {
                      if (readOnly || previousCorrectedStepsLength > 0) {
                        return;
                      }
                      setActionPlanChunk(actionPlanChunk => {
                        if (!actionPlanChunk) {
                          return actionPlanChunk;
                        } else {
                          return { ...actionPlanChunk, rating: value };
                        }
                      });
                    }}
                    variant={isSelected ? variant : 'transparent'}
                    // TODO ignore if we are not chaining processes together
                    disabled={readOnly || previousCorrectedStepsLength > 0}
                    className={twMerge(
                      `inline-flex px-2.5 py-1.5 text-sm items-center rounded-lg font-medium transition-all justify-center`,
                    )}
                  >
                    <Typography.Subtitle3>{buttonLabel}</Typography.Subtitle3>
                  </Button>
                );
              })}
            </div>
          )}
        </div>
        {/* NOTE: renders the edit view of action plan */}
        {actionPlanChunk.rating === -1 && (
          <div className="flex flex-col gap-2 items-start w-full border border-neutral-200 rounded-lg p-4">
            <MarkdownEditorContext
              defaultTokens={markdownSyntaxConfig}
              initialContent={actionPlanChunk.rewrite || actionPlanChunk.content}
            >
              <EditorContainer>
                <MarkdownToolbar />
                <MarkdownFloatingMenu />
                <Editor
                  content={actionPlanChunk.rewrite || actionPlanChunk.content}
                  onContentChange={newValue => {
                    if (previousCorrectedStepsLength > 0 || readOnly) {
                      return;
                    }
                    setActionPlanChunk(actionPlanChunk => {
                      if (!actionPlanChunk) {
                        return actionPlanChunk;
                      } else {
                        return { ...actionPlanChunk, rewrite: newValue };
                      }
                    });
                  }}
                  readOnly={previousCorrectedStepsLength > 0 || readOnly}
                />
              </EditorContainer>
            </MarkdownEditorContext>
          </div>
        )}
      </div>
    );
    if (correctedSteps?.length) {
      actionPlanComponent = (
        <Accordion
          key={`accordion-0`}
          header={
            <Tag className="" onClick={function noRefCheck() {}} size="lg" variant="neutral">
              STEP 0
            </Tag>
          }
          target={[actionPlanComponent]}
        />
      );
    }
    processStepComponents = [actionPlanComponent].concat(processStepComponents);
  }

  useEffect(() => {
    const errorMessage = getErrorMessage();
    setError(errorMessage);
  }, [getErrorMessage]);

  const maybeSubmitResponse = useCallback(
    (skipResetHistory: boolean = false) => {
      if (error) {
        return;
      }

      // Add back Step X and the substep key to the processSteps
      // Looping backwards because it is crucial that we see the raw tool use in a previous step when constructing the action input step
      // NOTE: takes the whole history of react steps, makes it into a format that can be parsed again in the backend
      // used for the model completion. Can only send one big string to the backend. Can't send a list of jsons

      // Open Question: how do we want to run the tools itself. Allows you to verify that the observation is what you want
      const processStepsCopy = processSteps
        .slice()
        .reverse()
        .map((chunk, index) => {
          let prepend = '';
          const chunkCopy = _.cloneDeep(chunk);
          const correctedIndex = processSteps.length - index - 1;
          if (correctedIndex % subChunkingKeys.length === 0) {
            prepend += `**Step ${
              Math.floor((correctedSteps?.length || 0 + index) / subChunkingKeys.length) + 1
            }:**\n\n`;
          }
          prepend += `**${subChunkingKeys[correctedIndex % subChunkingKeys.length]}**: `;

          if (chunk.rating === -1) {
            if (correctedIndex % subChunkingKeys.length === 2) {
              const selectedTool =
                processSteps[correctedIndex - 1].rewrite ||
                processSteps[correctedIndex - 1].content;
              const paramsJSONConstruct = `{ ${_.compact(
                toolParamsMap[selectedTool].map((param: string) => {
                  if (!chunk[`${selectedTool}$${param}`]) {
                    return undefined;
                  } else {
                    return `"${param}": "${chunk[`${selectedTool}$${param}`]}"`;
                  }
                }),
              ).join(',')} }`;
              chunkCopy.rewrite = prepend + paramsJSONConstruct;
            } else {
              chunkCopy.rewrite = prepend + (chunkCopy.rewrite || chunkCopy.content);
            }
          }
          chunkCopy.content = prepend + chunk.content;
          return chunkCopy;
        })
        .reverse();

      const correctedStepsCopy = correctedSteps?.map((chunk, index) => {
        let prepend = '';
        const chunkCopy = _.cloneDeep(chunk);
        if (index % subChunkingKeys.length === 0) {
          prepend += `**Step ${Math.floor(index / subChunkingKeys.length) + 1}:**\n\n`;
        }
        prepend += `**${subChunkingKeys[index % subChunkingKeys.length]}**: `;
        chunkCopy.content = prepend + chunk.content;
        if (chunk.rewrite) {
          chunkCopy.rewrite = prepend + chunk.rewrite;
        }
        return chunkCopy;
      });

      const actionPlanChunkCopy = _.cloneDeep(actionPlanChunk);

      actionPlanChunkCopy?.content &&
        (actionPlanChunkCopy.content = `**Step 0:**\n ${actionPlanChunkCopy.content}`);
      actionPlanChunkCopy?.rewrite &&
        (actionPlanChunkCopy.rewrite = `**Step 0:**\n ${actionPlanChunkCopy.rewrite}`);

      // TODO have this be related to whatever is marked as a "good" step
      let correctChunks: any[] = processStepsCopy.filter(
        (chunk, index) => index <= firstBadRatingIndex - 1,
      );
      let correctChunksAggregated: (IProcessSupervisionChunk | undefined)[];

      if (previousCorrectedStepsLength === 0) {
        if (actionPlanChunkCopy?.rating === -1) {
          correctChunks = [actionPlanChunkCopy];
          correctChunksAggregated = [actionPlanChunkCopy];
        } else {
          correctChunks = [actionPlanChunkCopy].concat(correctChunks);
          correctChunksAggregated = correctChunks;
        }
      } else {
        correctChunksAggregated = correctedStepsCopy?.concat(correctChunks) || correctChunks;
        correctChunksAggregated = [actionPlanChunkCopy].concat(correctChunksAggregated);
      }

      // Construct the preview/output of the response
      // @ts-ignore
      // Note: what gets displayed on the step in the preview when it's not clicked in.
      const output = experimentalGetStepOutput(
        actionPlanChunkCopy,
        processStepsCopy,
        firstBadRatingIndex,
        previousCorrectedStepsLength,
      );

      // NOTE: Check if all of the steps are labeled, or if the question is marked as solved.
      const isComplete = experimentalToolUseIsComplete(processStepsCopy, isQuestionSolvedByRewrite);

      const shouldContinue = !isComplete && reRequestModel;

      let chunks = processStepsCopy.map((chunk, index) => {
        // only keep the fields that are relevant to the chunk
        const fieldsToCollectAtIndex = getFieldsToCollectAtIndex(index);
        const allFields = coreFields.concat(fieldsToCollectAtIndex.map(field => field.fieldId));
        const newChunk: any = {
          content: chunk.content,
        };
        for (const fieldId of allFields) {
          newChunk[fieldId] = fieldEntries?.[fieldId]?.[index];
        }
        return newChunk;
      });

      chunks = [actionPlanChunkCopy].concat(correctedStepsCopy).concat(chunks);

      // Question: What is the format?
      const responseToSubmit: any = {
        type: InteractionStepType.ExperimentalToolUse,
        output,
        context: {
          shouldContinue,
          message: candidate?.message,
          candidate,
          chunks,
          isQuestionSolvedByRewrite,
          correctChunks,
          correctChunksAggregated,
          prompt: mostRecentPrompt?.output,
          multiturnContinueOutput: mostRecentPrompt?.output,
        },
      };

      // TODO: Do we need this? Launched without. If they edit an old step, data can get out of whack
      // Remove for now
      const shouldResetHistory = skipResetHistory
        ? false
        : getExperimentalToolUseShouldResetHistory(
            response,
            responseToSubmit,
            shouldContinue,
            reRequestModel,
          ) && shouldResetHistoryFeatureFlagEnabled;

      onSubmit(responseToSubmit, responseIndex, step, shouldResetHistory, false);
    },
    [
      complete,
      ratings,
      processSteps,
      candidate,
      error,
      previousResponses,
      firstBadRatingIndex,
      isQuestionSolvedByRewrite,
      reRequestModel,
      mostRecentPrompt?.output,
      response,
      onSubmit,
      responseIndex,
      step,
      getFieldsToCollectAtIndex,
      coreFields,
      fieldEntries,
    ],
  );

  // Skips the entire turn
  // Question: When do we skip the entire turn?
  const skipTurn = useCallback(() => {
    if (!step.params.allow_skip) {
      return;
    }
    onSubmit(
      {
        type: InteractionStepType.ExperimentalToolUse,
        output: processSteps.map(chunk => chunk.content).join('\n'),
        context: {
          shouldContinue: true,
          contextType: ContextType.Skipped,
        },
      },
      responseIndex,
      step,
      undefined,
    );
  }, [onSubmit, responseIndex, step, processSteps]);

  return (
    <StepWidthWrapper width={isPinnedView ? StepWidth.Full : StepWidth.Responsive}>
      <StepCard readOnly={readOnly}>
        <StepCard.Header>
          <StepCard.HeaderLeft>
            <StepCard.Icon icon="award" />
            <StepCard.HeaderColumn>
              <StepCard.Title>{step.title || DEFAULT_PROCESS_SUPERVISION_TITLE}</StepCard.Title>
              <StepCard.Subtitle>
                {step.subtitle || DEFAULT_PROCESS_SUPERVISION_SUBTITLE}
              </StepCard.Subtitle>
            </StepCard.HeaderColumn>
          </StepCard.HeaderLeft>
          <StepCard.HeaderRight>
            {!taskContext.enableUnifiedFeedback && (
              <CommentButton
                visible={true}
                childId={constructStepChildId(step)}
                turnIndex={step.turn}
              />
            )}
            <PinButton responseIndex={responseIndex} />
            <ChatMessageDisplayConfigButton
              chatMessageDisplayConfig={chatMessageDisplayConfig}
              useMarkdownDisplay={useMarkdownDisplay}
              updateMarkdownConfig={updateMarkdownConfig}
              toggleChatMessageDisplayType={toggleChatMessageDisplayType}
            />
          </StepCard.HeaderRight>
        </StepCard.Header>
        <StepCard.Body className="flex flex-col">
          {processStepsIsLoading ? (
            <div>
              {Array.from({ length: 10 }, (_, index) => (
                <Skeleton key={index} className="w-full mb-2 h-8 rounded" />
              ))}
            </div>
          ) : processStepsError ? (
            <Alert variant="warning" className="my-2">
              <Typography.Paragraph2>
                Error loading steps: {processStepsError as unknown as React.ReactNode}
              </Typography.Paragraph2>
            </Alert>
          ) : (
            <>
              {!processSteps?.length && !isEditingProcessSteps && (
                <>
                  <Alert variant="warning" className="my-2">
                    <Typography.Paragraph2>
                      Error chunking model response (the model response does not match the expected
                      format). Please resubmit the previous steps to generate a new response, or
                      manually edit the chunks to split up the response.
                    </Typography.Paragraph2>
                  </Alert>
                  <div className="bg-neutral-100 p-4 rounded-md">
                    <Markdown
                      markdown={candidate?.message?.content || ''}
                      enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
                      enableHtml={taskContext.htmlMarkdownConfig?.enablePromptPreviews}
                      enableSingleDollarTextMath={
                        chatMessageDisplayConfig.markdownConfig.enableSingleDollarTextMath
                      }
                      enableSoftBreaks={true}
                    />
                  </div>
                  <br />
                </>
              )}
              {isEditingProcessSteps && (
                <ProcessStepsEditor
                  processSteps={processSteps}
                  setProcessSteps={setProcessSteps}
                  originalContent={candidate?.message?.content}
                  onFinish={() => setIsEditingProcessSteps(false)}
                />
              )}
              {!isEditingProcessSteps && processStepComponents}
              {error && (
                <Alert variant="warning" className="my-2">
                  <Typography.Paragraph2>{error}</Typography.Paragraph2>
                </Alert>
              )}
            </>
          )}
        </StepCard.Body>
        {!processStepsIsLoading && !processStepsError && !isEditingProcessSteps && (
          <StepCard.Footer className="flex flex-row-reverse gap-2 w-full">
            {!taskContext.previewMode && (
              <Button
                variant="primary"
                onClick={() => {
                  maybeSubmitResponse(false);
                }}
                disabled={!!error || (!processSteps?.length && !isEditingProcessSteps)}
              >
                <Icon
                  variant="white"
                  icon="check"
                  // @ts-ignore
                  size="sm"
                  className={`mr-2 ${error ? 'opacity-50' : ''}`}
                />
                Save and Continue
              </Button>
            )}
            {!taskContext.previewMode && step.params?.enable_edit_without_reroll && (
              <Button
                variant="secondary"
                onClick={() => {
                  if (
                    window.confirm(
                      'Are you sure you want to save changes without regenerating the rest of the conversation?',
                    )
                  ) {
                    maybeSubmitResponse(true);
                  }
                }}
                disabled={!!error || (!processSteps?.length && !isEditingProcessSteps)}
              >
                <Icon
                  variant="secondary"
                  icon="wand-magic-sparkles"
                  // @ts-ignore
                  size="sm"
                  className={`mr-2 ${error ? 'opacity-50' : ''}`}
                />
                Save without Regenerating
              </Button>
            )}
            {step.params?.allow_skip && (
              <Button
                variant="neutral"
                onClick={() => {
                  if (window.confirm('Are you sure you want to skip this step?')) {
                    skipTurn();
                  }
                }}
              >
                {/* @ts-ignore */}
                <Icon icon="forward" size="sm" className="mr-2" />
                Skip
              </Button>
            )}
            {!step.params.disable_edit_chunks && (
              <Button
                variant="neutral"
                onClick={() => {
                  setIsEditingProcessSteps(true);
                }}
              >
                {/* @ts-ignore */}
                <Icon icon="edit" size="xs" className="mr-2" />
                Edit chunks
              </Button>
            )}
          </StepCard.Footer>
        )}
      </StepCard>
    </StepWidthWrapper>
  );
};

const ProcessStepsEditor = ({
  processSteps,
  setProcessSteps,
  originalContent,
  onFinish,
}: {
  processSteps: ProcessSupervisionChunk[];
  setProcessSteps: (processSteps: ProcessSupervisionChunk[]) => void;
  originalContent: string;
  onFinish: () => void;
}) => {
  const { chatMessageDisplayConfig } = useConfigureChatMessageDisplay();
  const taskContext = useContext(ChatTaskContext);

  const [localProcessSteps, setLocalProcessSteps] = useState(
    processSteps.length ? [...processSteps] : [{ content: '' }],
  );
  const numSteps = localProcessSteps.length;

  const handleFinish = () => {
    setProcessSteps(localProcessSteps);
    onFinish();
  };

  const error = useMemo(() => {
    for (const step of localProcessSteps) {
      if (!step?.content) {
        return `Please fill out all steps`;
      }
    }

    return '';
  }, [localProcessSteps]);

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }
      const startIndex = result.source.index;
      const endIndex = result.destination.index;
      const newProcessSteps = [...localProcessSteps];
      const [removed] = newProcessSteps.splice(startIndex, 1);
      newProcessSteps.splice(endIndex, 0, removed);
      setLocalProcessSteps(newProcessSteps);
    },
    [localProcessSteps],
  );

  return (
    <div className="flex flex-col gap-2">
      <Typography.Subtitle1>
        Please split the following message into chunks and ensure that all of the original content
        is preserved.
      </Typography.Subtitle1>
      <Markdown
        key="originalContent"
        markdown={originalContent}
        className="bg-neutral-100 p-4 rounded-md"
        enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
        enableHtml={taskContext.htmlMarkdownConfig?.enablePromptPreviews}
        enableSingleDollarTextMath={
          chatMessageDisplayConfig.markdownConfig.enableSingleDollarTextMath
        }
      />
      <div className="flex flex-col gap-2">
        <Typography.Subtitle1>Chunks</Typography.Subtitle1>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="flex flex-col gap-2"
              >
                {Array.from({ length: numSteps }, (_, index) => {
                  return (
                    <Draggable key={`${index}}`} draggableId={`draggable-${index}`} index={index}>
                      {(provided, snapshot) => (
                        <div
                          className="flex items-center gap-2"
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          <div {...provided.dragHandleProps} style={{ cursor: 'grab' }}>
                            <Icon variant="neutral" size="md" icon="grip-vertical" />
                          </div>
                          <TextArea
                            key={index}
                            id={`processStep-${index}`}
                            value={localProcessSteps?.[index]?.content || ''}
                            onChange={e => {
                              const newLocalProcessSteps = [...localProcessSteps];
                              newLocalProcessSteps[index] = {
                                ...newLocalProcessSteps[index],
                                content: e.target.value,
                                isEdited: true,
                              };
                              setLocalProcessSteps(newLocalProcessSteps);
                            }}
                            dynamic
                            className="flex flex-1 w-full"
                            textAreaWrapperClassName="flex flex-1"
                            placeholder={`(e.g.) Step ${index + 1}: ...`}
                          />
                          <Button
                            onClick={() => {
                              const newLocalProcessSteps = [...localProcessSteps];
                              newLocalProcessSteps.splice(index, 1);
                              setLocalProcessSteps(newLocalProcessSteps);
                            }}
                            variant="neutral"
                            className="p-1"
                          >
                            <Icon icon="trash" variant="neutral" size="sm" />
                          </Button>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
      {/* add another step */}
      <Button
        onClick={() => {
          setLocalProcessSteps([...localProcessSteps, { content: '' }]);
        }}
        variant="primary"
        className="max-w-[160px]"
      >
        {/* @ts-ignore */}
        <Icon icon="plus" className="mr-2" variant="white" />
        Add another step
      </Button>
      {error && (
        <Alert variant="warning" className="my-2">
          <Typography.Paragraph2>{error}</Typography.Paragraph2>
        </Alert>
      )}

      <div className="flex flex-row-reverse gap-2 w-full mb-2">
        <Button variant="primary" onClick={handleFinish} disabled={!!error}>
          {/* @ts-ignore */}
          <Icon variant="white" icon="check" size="sm" className="mr-2" />
          Save chunks
        </Button>
      </div>
    </div>
  );
};

export default memo(ExperimentalToolUseTaskComponent);
